///
/// EarthBrowser
///
/// Created by Matt Giger on 10/27/2026
/// Copyright (c) 2026 Geodata Labs
///
/// GOES GLM lightning for the storm column. The clouds dataset publishes a
/// "lightning" shard (weather-services storm-lightning.ts: one row per GLM
/// flash inside an active storm's 16° box over the last 48 h), which the
/// app otherwise only downloads in video render mode. The column fetches
/// it on demand when a block opens, keeps the rows for its storm in memory
/// while the block is up, and turns them into sparks on the lid plus the
/// hourly inner-core / outer-band counts the HUD reads. Eyewall lightning
/// bursts often precede intensification (DeMaria et al. 2012); outer-band
/// lightning is routine.
///

import { Constants } from "@/constants"
import { type DatasetManifest } from "@/earthbrowser/dataset-distribution/DatasetLayer"
import { type StormTrackPoint } from "@/earthbrowser/dataset-distribution/hurricanes/HurricanesTable"
import { stormStateAtTime } from "@/earthbrowser/dataset-distribution/hurricanes/stormTrack"
import { type StormCutoutLightning } from "@/redux/dialogSlice"
import { datasetUrl } from "@/utils/datasetUrl"
import { tableFromIPC } from "apache-arrow"
import { localOffsetKm } from "./stormColumnLayout"

export interface StormColumnFlash {
	/** First event time, epoch ms */
	t: number
	lat: number
	lon: number
	/** Radiant energy, joules */
	energy: number
}

const cCloudsManifestUrl = `${Constants.baseStorageUrl}/datasets/clouds/manifest.json`
/** Inner core: within this distance of the center at flash time (DeMaria et al. 2012 use 0–100 km) */
export const cInnerCoreKm = 100
/** GLM fields of view: GOES-East and GOES-West together see the Americas and the oceans on both sides */
const cGlmLonMin = -179
const cGlmLonMax = -8
const cGlmLatMax = 55
const cHourMs = 3600_000

/**
 * Lazily downloaded lightning shard, grouped by storm. One copy lives while
 * a column is open (release() drops it); a new clouds checkpoint moves the
 * shard URL, so the cache key is the URL itself.
 */
export class StormLightningSource {
	private static cache?: { url: string; rows: Promise<Map<string, StormColumnFlash[]>> }

	/**
	 * Flashes for one storm, oldest first. `shard: false` when the clouds
	 * dataset currently publishes no lightning at all (no GOES-visible storm)
	 */
	public static async flashesForStorm(stormId: string): Promise<{ flashes: StormColumnFlash[]; shard: boolean }> {
		const response = await fetch(datasetUrl(cCloudsManifestUrl), { cache: "no-cache" })
		if (!response.ok) throw new Error(`clouds manifest ${response.status}`)
		const manifest = await response.json() as DatasetManifest
		const shard = manifest.checkpoint?.shards?.find(s => s.key === "lightning")
		if (!shard) return { flashes: [], shard: false }
		const url = shard.url ?? `${Constants.baseStorageUrl}/${shard.path}`
		if (this.cache?.url !== url) {
			this.cache = { url, rows: this.download(url) }
		}
		const byStorm = await this.cache.rows
		return { flashes: byStorm.get(stormId) ?? [], shard: true }
	}

	public static release(): void {
		this.cache = undefined
	}

	private static async download(url: string): Promise<Map<string, StormColumnFlash[]>> {
		const response = await fetch(datasetUrl(url), { cache: "no-cache" })
		if (!response.ok) throw new Error(`lightning shard ${response.status}`)
		const table = tableFromIPC(new Uint8Array(await response.arrayBuffer()))
		const byStorm = new Map<string, StormColumnFlash[]>()
		for (const row of table.toArray() as Array<{ storm_id: unknown; t: unknown; lat: unknown; lon: unknown; energy: unknown }>) {
			const flash = { t: Number(row.t), lat: Number(row.lat), lon: Number(row.lon), energy: Number(row.energy) }
			if (!Number.isFinite(flash.t) || !Number.isFinite(flash.lat) || !Number.isFinite(flash.lon)) continue
			const id = String(row.storm_id)
			let list = byStorm.get(id)
			if (!list) {
				list = []
				byStorm.set(id, list)
			}
			list.push(flash)
		}
		for (const list of byStorm.values()) list.sort((a, b) => a.t - b.t)
		return byStorm
	}
}

/** Whether a GOES lightning mapper sees this position at all */
export function glmCovers(lat: number, lon: number): boolean {
	return lon >= cGlmLonMin && lon <= cGlmLonMax && Math.abs(lat) <= cGlmLatMax
}

/** Distance from the storm center at the flash time, km (center interpolated along the observed track) */
function distanceFromCenterKm(flash: StormColumnFlash, observed: StormTrackPoint[], fallback: { lat: number; lon: number }): number {
	const center = stormStateAtTime(observed, flash.t) ?? fallback
	const local = localOffsetKm(flash.lat, flash.lon, center.lat, center.lon)
	return Math.hypot(local.east, local.north)
}

/**
 * Hourly flash counts split at the inner-core radius, the hour ending at
 * the reference time (the scene clock, clamped into the data) against the
 * hour before it, and the peak hour. `flashes` oldest first
 */
export function lightningSummary(
	flashes: StormColumnFlash[],
	observed: StormTrackPoint[],
	center: { lat: number; lon: number },
	referenceMs: number,
	covered: boolean
): StormCutoutLightning {
	if (flashes.length === 0) {
		return { covered, total: 0, spanHours: 0, lastHour: 0, priorHour: 0, innerCoreLastHour: 0, innerCoreKm: cInnerCoreKm, hourly: [], referenceT: referenceMs }
	}
	const oldest = flashes[0].t
	const newest = flashes[flashes.length - 1].t
	const reference = Math.min(Math.max(referenceMs, oldest), newest)
	// Bins run back from the reference time so "the last hour" is a whole bin
	const binCount = Math.ceil((reference - oldest) / cHourMs) + 1
	const hourly: Array<{ t: number; count: number; innerCore: number }> = []
	for (let i = binCount - 1; i >= 0; i--) {
		hourly.push({ t: reference - i * cHourMs, count: 0, innerCore: 0 })
	}
	let lastHour = 0
	let priorHour = 0
	let innerCoreLastHour = 0
	for (const flash of flashes) {
		if (flash.t > reference) break
		const age = reference - flash.t
		const bin = hourly[binCount - 1 - Math.floor(age / cHourMs)]
		if (!bin) continue
		const inner = distanceFromCenterKm(flash, observed, center) <= cInnerCoreKm
		bin.count++
		if (inner) bin.innerCore++
		if (age < cHourMs) {
			lastHour++
			if (inner) innerCoreLastHour++
		} else if (age < 2 * cHourMs) {
			priorHour++
		}
	}
	let peak: { t: number; count: number } | undefined
	for (const bin of hourly) {
		if (!peak || bin.count > peak.count) peak = { t: bin.t, count: bin.count }
	}
	return {
		covered,
		total: flashes.length,
		spanHours: Math.round((newest - oldest) / cHourMs * 10) / 10,
		lastHour,
		priorHour,
		innerCoreLastHour,
		innerCoreKm: cInnerCoreKm,
		hourly,
		peak: peak && peak.count > 0 ? peak : undefined,
		referenceT: reference
	}
}
